// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback } from 'react';

import type { EvolveApiRoute, EvolveApiResponse } from '@evolvemep/api-ts';
import { useUncontrolled } from '@mantine/hooks';

import type { WrappedListProps, TypeSafeRenderOption } from 'components/Mantine/TypeSafeSelect/types';
import { WrappedList } from 'components/Mantine/TypeSafeSelect/WrappedList';
import type { KeysOfType } from 'helpers/helper-types';
import { isNotNil } from 'helpers/isNotNil';

import type { SearchableSelectorProps } from './types';
import { useSearchableData } from './useSearchableData';

type Props<
  Route extends EvolveApiRoute<'GET_PAGINATED'>,
  T extends Partial<EvolveApiResponse<'GET_PAGINATED', Route>['data'][number]>,
  TKey extends KeysOfType<T, string>,
> = SearchableSelectorProps<Route, T, TKey> &
  Omit<WrappedListProps<string, object>, keyof SearchableSelectorProps<Route, T, TKey> | 'data' | 'renderOption'> & {
    renderOption?: TypeSafeRenderOption<string, { original: T }>;
  };

/**
 * Used for Check Lists whose "search" functionality
 * involves fetching data from the backend.
 */
export const SearchableList = <
  Route extends EvolveApiRoute<'GET_PAGINATED'>,
  T extends Partial<EvolveApiResponse<'GET_PAGINATED', Route>['data'][number]>,
  TKey extends KeysOfType<T, string>,
>({
  paginatedGet,
  getItemLabel,
  idKey,
  value: parentValue,
  onChange: parentOnChange,
  excludeOption,
  searchKey,
  useLike,
  defaultValue: parentDefaultValue,
  onOptionSubmit,
  disableOption,
  ...props
}: Props<Route, T, TKey>) => {
  const { options, fetchNextPage, loading, searchPhrase, pendingSearchPhrase, setSearchPhrase } = useSearchableData({
    paginatedGet,
    searchKey,
    useLike,
    excludeOption,
  });

  const [selectedOption, setSelectedOption] = useUncontrolled<T | null>({
    value: parentValue,
    defaultValue: parentDefaultValue,
    onChange: (v) => {
      parentOnChange(v);
      if (v) onOptionSubmit?.(v);
    },
  });

  // Override the WrappedCheckList's default filtering
  // since we're handling it server side
  const filterOverride = useCallback(() => true, []);

  return (
    <WrappedList
      {...props}
      disableOption={
        isNotNil(disableOption)
          ? (v) => {
              const opt = options.find((o) => o[idKey] === v);
              return isNotNil(opt) ? disableOption(opt) : false;
            }
          : undefined
      }
      onChange={(v) => setSelectedOption(options.find((o) => o[idKey] === v) ?? null)}
      onOptionSubmit={(id) => {
        if (isNotNil(onOptionSubmit)) {
          const opt = options.find((o) => o[idKey] === id) ?? selectedOption;
          if (opt) onOptionSubmit(opt);
        }
      }}
      value={selectedOption?.[idKey] as string}
      data={options.map((o) => ({ value: o[idKey] as string, label: getItemLabel(o), original: o }))}
      loading={loading}
      onScrollToEnd={fetchNextPage}
      filterInclude={filterOverride}
      onSearchChange={setSearchPhrase}
      searchValue={searchPhrase}
      pendingSearchValue={pendingSearchPhrase}
      flexListProps={{ gap: 'md' }}
    />
  );
};
